<script lang="ts" setup>
import SingleSelectInput from '@/corePagesModule/SelectInput/SingleSelectInput.vue';
import { i18n } from '@/i18n';
import { computed } from 'vue';
import { useForms } from '@/corePagesModule/composables/useForm';
import { FieldItem } from '@/coreDomainsModule/constant/validateRules';
import { ref, watch } from 'vue';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import AddOptionSection from './AddOptionSection.vue';
import { useUpdateBusinessModalStore } from '../useUpdateBusinessModalStore';
import { storeToRefs } from 'pinia';
import { validateRuleKeyMap, keyMappingForSelectInput, formOptionsBusinessCreate, inputTypeKeyMap } from '../constants';
import { BusinessFreeColumnValue, IBusiness } from '@/coreDomainsModule/entities/businessBase';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { objectDiff } from '@/utils/objectDiff';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { UserAccount } from '@/coreDomainsModule/entities/users';
import MultipleUserSelectInput from '@/corePagesModule/user/SelectInput/MultipleUserSelectInput.vue';
import { useUpdateFieldState } from '../composables/useUpdateFieldState';
import { UpdateBusinessViewModel } from '../UpdateBusinessViewModel';
import { diContainer } from '@/coreContainer/container';
import MultiTagInput from '@/corePagesModule/components/MultiTagInput.vue';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';

const t = i18n.t;
const updateBusinessVM = new UpdateBusinessViewModel();
const store = useUpdateBusinessModalStore();
const { state } = storeToRefs(store);
const { getAddableOptions } = store;

const props = withDefaults(
  defineProps<{
    item?: IBusiness;
  }>(),
  { item: undefined }
);

const emits = defineEmits<{
  (e: 'close', modal: string, isNext?: boolean): void;
}>();

const isEdited = ref<boolean>(false);

const fieldItems = computed((): FieldItem[] => {
  const targetArray = formOptionsBusinessCreate;
  const items = targetArray.map((name: string) => {
    return {
      name,
      rules: validateRuleKeyMap[name],
    };
  });
  return items;
});

const { fieldState, allValidate, resetForm } = useForms(fieldItems.value);
const { addNewOption, updateValue, updateUserValue } = useUpdateFieldState(fieldState, isEdited);

const currentOpenSelectInputName = ref('');
const baseFreeColumnSettings = ref<Omit<BusinessFreeColumnValue, 'value'>[]>([]);
const businessFreeColumnValues = ref<BusinessFreeColumnValue[]>([]);

/**
 * 自由欄設定（関連取引）を取得し、設定済みの項目だけを保持する。columnNo は 1 から採番する。
 */
const fetchBaseFreeColumnSettings = async () => {
  const result = await diContainer.settingRepository.getBusinessFreeColumnSetting();
  if (result.status.type !== 'success') {
    baseFreeColumnSettings.value = [];
    return;
  }
  baseFreeColumnSettings.value = result.status.data.base
    .map((item, index) => ({
      columnNo: index + 1,
      headerName: item.headerName,
      isMulti: item.isMulti,
      isConfigured: item.isConfigured ?? false,
      target: 'base' as const,
    }))
    .filter((item) => item.isConfigured);
};

/**
 * 自由欄の入力欄を編集対象の現在値でプリフィルする
 */
const initializeBusinessFreeColumns = () => {
  const stateValues = state.value.item?.businessFreeColumnValues;
  const sourceValues = stateValues?.some((column) => column.target === 'base') ? stateValues : props.item?.businessFreeColumnValues;
  businessFreeColumnValues.value = baseFreeColumnSettings.value.map((setting) => ({
    ...setting,
    value:
      sourceValues?.find((column) => (column.target === 'base' || column.target === undefined) && column.columnNo === setting.columnNo)
        ?.value ?? '',
  }));
};

/**
 * 自由欄の入力値を更新する
 * @param index - 自由欄配列のindex
 * @param value - 入力値
 */
const updateBusinessFreeColumnValue = (index: number, value: string) => {
  const target = businessFreeColumnValues.value[index];
  if (!target) return;
  target.value = value;
  isEdited.value = true;
};

const baseFreeColumnSettingsReady = fetchBaseFreeColumnSettings();

/**
 * 編集モーダルの保存ボタン押下時の処理
 */
const editingModalOnNext = async () => {
  if (allValidate()) {
    const baseBusinessResponsibleUsersString = fieldState['baseBusinessResponsibleUser'].value.value;
    const aggregateFreeColumnValues = state.value.item?.businessFreeColumnValues?.filter((column) => column.target === 'aggregate') ?? [];
    const updatedItem = BusinessFactory.create({
      ...props.item,
      warehouseNumber: fieldState['warehouseNumber'].value.value,
      warehouse: fieldState['warehouseName'].value.value,
      supplierNumber: fieldState['supplierNumber'].value.value,
      supplierName: fieldState['supplierName'].value.value,
      businessNumber: fieldState['businessNumber'].value.value,
      businessName: fieldState['businessName'].value.value,
      package: fieldState['package'].value.value,
      customerNumber: fieldState['customerNumber'].value.value,
      customerName: fieldState['customerName'].value.value,
      salesDestinationNumber: fieldState['salesDestinationNumber'].value.value,
      salesDestinationName: fieldState['salesDestinationName'].value.value,
      endUser: updateBusinessVM.convertUnassingedToUndefined(fieldState['endUser'].value.value),
      baseBusinessResponsibleUsers: baseBusinessResponsibleUsersString
        ? baseBusinessResponsibleUsersString.split(',').map((userId) => UserAccountFactory.create({ email: userId }))
        : [],
      businessFreeColumnValues: [
        ...aggregateFreeColumnValues.map((column) => ({ ...column })),
        ...businessFreeColumnValues.value.map((column) => ({ ...column })),
      ],
    });
    const updatedDiff = objectDiff(props.item ?? {}, updatedItem);
    state.value.updatedItemDiff = updatedDiff.first;

    state.value.item = updatedItem;
    emits('close', 'baseBusinessEdit', true);
  }
};

/**
 * モーダルを開いた際の処理
 */
watch(
  () => [state.value.modalState.baseBusinessEdit, state.value.isCreateMode],
  async ([stateNewVal, isCreateModeNewBal]) => {
    if (stateNewVal && isCreateModeNewBal && props.item) {
      await baseFreeColumnSettingsReady;
      if (!state.value.modalState.baseBusinessEdit || !state.value.isCreateMode) return;
      resetForm(fieldItems.value);
      for (let i = 0; i < fieldItems.value.length; i++) {
        const keyName = fieldItems.value[i].name;
        if (keyName === 'warehouseName') {
          fieldState[keyName].setValue(state.value.item?.['warehouse'] ?? props.item['warehouse'], false);
        } else if (keyName === 'baseBusinessResponsibleUser') {
          fieldState[keyName].setValue(
            state.value.item?.baseBusinessResponsibleUsers?.map((user: UserAccount) => user.email).join(',') ??
              props.item.baseBusinessResponsibleUsers?.map((user: UserAccount) => user.email).join(',') ??
              '',
            false
          );
        } else {
          fieldState[keyName].setValue(state.value.item?.[keyName] ?? props.item[keyName] ?? '', false);
        }
      }
      initializeBusinessFreeColumns();
    }
  }
);

/**
 * inputのgrid幅指定クラス取得
 * @param option - inputの対応名
 */
const getBaseDataItemClass = (option: string) => {
  if (option === 'package' || option === 'baseBusinessResponsibleUser') {
    return 'modal__item--number';
  } else {
    return 'modal__item--normal';
  }
};

/**
 * 自由入力カラムの編集状態を変更する
 */
const markBusinessFreeColumnEdited = () => {
  isEdited.value = true;
};
</script>
<template>
  <InfoModal
    :modal-state="state.modalState.baseBusinessEdit && state.isCreateMode"
    :modal-width="1300"
    :back-button-label="t('キャンセル')"
    :next-button-label="t('保存')"
    :is-disabled-next-button="!isEdited"
    @on-back="
      () => {
        emits('close', 'baseBusinessEdit', false);
      }
    "
    @on-next="editingModalOnNext"
  >
    <template #title>{{ t('取引追加') }}</template>
    <template #description>{{ t('新規に取引を追加できます。') }}</template>
    <template #default>
      <div class="modal__container">
        <template v-for="(option, index) in formOptionsBusinessCreate" :key="option">
          <div class="modal__item" :class="getBaseDataItemClass(option)">
            <template v-if="inputTypeKeyMap[option] === 'selectUser'">
              <MultipleUserSelectInput
                :select-input-index="index"
                :label="t(option)"
                :placeholder="t(option)"
                :original-selected-user-id-list="fieldState[option].value.value ? fieldState[option].value.value.split(',') : []"
                @update:model-value="(e) => updateUserValue(option, e)"
              />
            </template>
            <template v-else>
              <SingleSelectInput
                :index="index"
                :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                :model-value="fieldState[option].value.value"
                :placeholder="t(option)"
                :filter-type="keyMappingForSelectInput[option]"
                :is-error="fieldState[option]?.isError.value"
                :error-message="fieldState[option]?.errorMessage.value"
                @click:select-input="() => (currentOpenSelectInputName = option)"
                @update:model-value="(e) => updateValue(option, e)"
              />
            </template>
          </div>
        </template>
      </div>
      <div v-if="businessFreeColumnValues.length > 0" class="modal__container modal__container--free-column">
        <template v-for="(freeColumn, index) in businessFreeColumnValues" :key="freeColumn.columnNo">
          <div class="modal__item">
            <MultiTagInput
              v-if="freeColumn.isMulti"
              :model-value="freeColumn.value"
              :label="freeColumn.headerName"
              :placeholder="freeColumn.headerName"
              @editing="markBusinessFreeColumnEdited"
              @update:model-value="(e) => updateBusinessFreeColumnValue(index, e)"
            />
            <TextInput
              v-else
              :model-value="freeColumn.value"
              :label="freeColumn.headerName"
              :placeholder="freeColumn.headerName"
              ensure-error-msg-height
              @update:model-value="(e) => updateBusinessFreeColumnValue(index, e)"
            />
          </div>
        </template>
      </div>
      <!-- 新規選択肢追加 -->
      <AddOptionSection :options="getAddableOptions(false)" @set-option="addNewOption" />
    </template>
  </InfoModal>
</template>
<style lang="scss" scoped>
.modal {
  &__container {
    display: grid;
    width: 100%;

    padding: 16px;
    border-radius: 8px;
    column-gap: 8px;
    row-gap: 16px;

    background-color: $backgroundDefault;
    grid-template-columns: repeat(8, 1fr);
  }

  &__item {
    display: grid;
    grid-template-columns: 100%;
    height: fit-content;

    box-sizing: border-box;
    border-bottom: solid 1px $borderDefault;

    &--normal {
      grid-column: span 2;
    }
    &--number {
      grid-column: span 1;
    }
  }
}
</style>
